import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { GetOperationCategoriesResponse } from '@/api/handlers/resources/operations/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { OperationsQueryKeys } from './operationsQueryKeys';

/**
 * Six rows that change about once a year.
 *
 * Long `staleTime` on purpose: this is a taxonomy, not a feed. It is invalidated
 * by the `all` prefix every operations write already uses, so an editor that
 * renames a category still sees the new name — the cache is long, not stubborn.
 */
const STALE_TIME_MS = 10 * 60_000;

/**
 * The tenant's operation categories, in display order.
 *
 * `enabled` is the caller's, the pattern every accounting query here follows:
 * access is derived from the session before fetching, because a raw 403 tells
 * the user nothing they can act on.
 */
export const useOperationCategoriesQuery = (enabled: boolean) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetOperationCategoriesResponse>>({
        queryKey: OperationsQueryKeys.categories(),
        queryFn: () => apiClient.operations.getCategories(),
        enabled,
        staleTime: STALE_TIME_MS,
    });
};
